import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon/index.js';
import { CssBaseline, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { SnackbarProvider } from 'notistack';
import type { FC } from 'react';
import { StrictMode, Suspense, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './app.js';
import { MiddleSpinner } from './components/middle-spinner/index.js';
import { TitleProvider, useSetTitle } from './hooks/use-title.js';
import { UpdateProvider } from './hooks/use-update.js';
import './index.css';
import reportWebVitals from './report-web-vitals.js';
import { createAppTheme } from './theme.js';
import {
  getAppearanceSettings,
  onAppearanceSettingsChanged,
} from './utils/appearance-settings.js';
import { ProvideAuth } from './utils/auth/index.js';
import './utils/axios.js';

const Loading: FC = () => {
  useSetTitle('Patrol Roster ...');
  return <MiddleSpinner />;
};

const RootApp: FC = () => {
  const [appearance, setAppearance] = useState(getAppearanceSettings());

  useEffect(() => {
    return onAppearanceSettingsChanged(() => {
      setAppearance(getAppearanceSettings());
    });
  }, []);

  const theme = useMemo(() => createAppTheme(appearance), [appearance]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <ProvideAuth>
          <App />
        </ProvideAuth>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

if (window.location.hostname !== 'localhost') {
  const MATCH_FEATURE_FLAG = /flagsmith|hotjar|clarity/;

  Sentry.init({
    beforeBreadcrumb: (breadcrumb, hint) => {
      const xhr = hint?.xhr as XMLHttpRequest | undefined;
      return breadcrumb.category === 'xhr' &&
        xhr?.responseURL.match(MATCH_FEATURE_FLAG)
        ? null
        : breadcrumb;
    },

    denyUrls: [MATCH_FEATURE_FLAG],
    dsn: 'https://8a984851d5db4ce19b0c5c65c8591105@o4504547380232192.ingest.sentry.io/4504547382067200',

    environment: 'browser',
    ignoreErrors: ['AbortError', 'CanceledError', 'UnauthorizedError'],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest: (url) => {
          return !url.match(MATCH_FEATURE_FLAG);
        },
        useEffect,
        useLocation,
        useNavigationType,
      }),
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
      }),
      // Sentry.feedbackIntegration({
      //   colorScheme: 'dark',
      //   isEmailRequired: true,
      //   isNameRequired: true,
      //   useSentryUser: {
      //     email: 'email',
      //     name: 'name',
      //   },
      // }),
    ],

    release: import.meta.env.VITE_APP_SHA,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.5,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.01,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
  });

  Sentry.setExtras({
    geolocationSupported: 'geolocation' in window.navigator,
    intlLocale: new Intl.DateTimeFormat().resolvedOptions().locale,
    navigatorLanguage: window.navigator.language,
    navigatorLanguages: window.navigator.languages,
  });
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <TitleProvider>
      <Suspense fallback={<Loading />}>
        <UpdateProvider>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <RootApp />
          </LocalizationProvider>
        </UpdateProvider>
      </Suspense>
    </TitleProvider>
  </StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);
